import { clsx } from 'clsx';
import { useNotify } from 'react-admin';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Button, Col, Form, Modal, Row, Input, Select } from 'antd';

import { Manager, User } from '@types';
import { FormRow } from '@components';
import { dealerHttp, NewFarmerParams, userHttp } from '@network';
import { useDateMask, useDebounce, useNumberMask, usePhoneMask } from '@hooks';
import cls from './AddFarmer.module.css';


interface Props {
  manager: Manager;
  className?: string;
}

const initialValues = {
  firstName: '',
  middleName: '',
  lastName: '',
  gender: '',
  email: '',
  phone: '',
  zipCode: '',
  birthdate: '',
};
const genderOptions = [
  { value: 'Mujer', label: 'Mujer' },
  { value: 'Hombre', label: 'Hombre' },
];
const userTypeOptions = [
  { value: 'Fisica', label: 'Fisica' },
  { value: 'Moral', label: 'Moral' },
];
const MAX_PHONE_LENGTH = 15;
const MAX_ZIP_LENGTH = 6;
const MAX_NAME_LENGTH = 50;
const mainColGrid = {
  xxl: { title: 7, children: 17 },
  md: { title: 10, children: 14 },
  sm: { title: 8, children: 16 },
  xs: { title: 24, children: 24 },
};

const Icon = () => (
  <svg className={cls.messageIcon} width="1em" height="1em" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_6405_34976)">
      <path d="M15.7143 19.2857C15.7143 19.6786 15.3929 20 15 20H4.64286C2.07857 20 0 17.9214 0 15.3571C0 12.7929 2.07857 10.7143 4.64286 10.7143H15.3571C17.7714 10.6929 19.7929 12.5286 20 14.9357C20.0357 15.3286 19.7429 15.6786 19.35 15.7143C18.9571 15.75 18.6071 15.4571 18.5714 15.0643C18.4286 13.4 17.0286 12.1286 15.3571 12.1429H4.64286C2.86429 12.1429 1.42857 13.5786 1.42857 15.3571C1.42857 17.1357 2.86429 18.5714 4.64286 18.5714H15C15.3929 18.5714 15.7143 18.8929 15.7143 19.2857ZM5 5C5 2.23571 7.23571 0 10 0C12.7643 0 15 2.23571 15 5C15 7.76429 12.7643 10 10 10C7.23571 10 5 7.76429 5 5ZM6.42857 5C6.42857 6.97143 8.02857 8.57143 10 8.57143C11.9714 8.57143 13.5714 6.97143 13.5714 5C13.5714 3.02857 11.9714 1.42857 10 1.42857C8.02857 1.42857 6.42857 3.02857 6.42857 5Z" fill="white"/>
      <path d="M15.3572 10.7144C12.7929 10.7144 10.7143 12.7929 10.7143 15.3572C10.7143 17.9215 12.7929 20.0001 15.3572 20.0001C17.9214 20.0001 20 17.9215 20 15.3572C20 12.7929 17.9214 10.7144 15.3572 10.7144ZM17.4072 16.0429H16.0429V17.4072C16.0429 17.7858 15.7357 18.0929 15.3572 18.0929C14.9786 18.0929 14.6714 17.7858 14.6714 17.4072V16.0429H13.3072C12.9286 16.0429 12.6214 15.7358 12.6214 15.3572C12.6214 14.9786 12.9286 14.6715 13.3072 14.6715H14.6714V13.3072C14.6714 12.9286 14.9786 12.6215 15.3572 12.6215C15.7357 12.6215 16.0429 12.9286 16.0429 13.3072V14.6715H17.4072C17.7857 14.6715 18.0929 14.9786 18.0929 15.3572C18.0929 15.7358 17.7857 16.0429 17.4072 16.0429Z" fill="white"/>
    </g>
    <defs>
      <clipPath id="clip0_6405_34976">
        <rect width="20" height="20" fill="white"/>
      </clipPath>
    </defs>
  </svg>
);

export const AddFarmer = ({ manager, className }: Props) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [findUserByEmail, setFindUserByEmail] = useState<User | null>(null);
  const [findUserByPhone, setFindUserByPhone] = useState<User | null>(null);
  const notify = useNotify();
  const hasLoaded = useRef<boolean>(false);
  const [form] = Form.useForm();

  const [selectedGender, setSelectedGender] = useState<string>(initialValues.gender);
  const [selectedUserType, setSelectedUserType] = useState<string>('Fisica');
  const [companyName, setCompanyName] = useState<string>('');

  const firstName = Form.useWatch('firstName', form);
  const middleName = Form.useWatch('middleName', form);
  const lastName = Form.useWatch('lastName', form);
  const email = Form.useWatch('email', form);
  const phone = Form.useWatch('phone', form);
  const zipCode = Form.useWatch('zipCode', form);
  const birthdate = Form.useWatch('birthdate', form);

  const formattedPhone = phone?.replace(/\D/g, '') || '';
  const isPhoneValid = formattedPhone?.length === 10;
  const isZipCodeValid = zipCode?.length === 5 || zipCode?.length === 6;
  const formattedDate = birthdate?.replace(/\D/g, '') || '';
  const isBirthdayValid = formattedDate?.length === 8;
  const debouncedEmail = useDebounce(email, 500);
  const debouncedPhone = useDebounce(phone, 500);

  const phoneFormat = usePhoneMask();
  const numberFormat = useNumberMask();
  const dateFormat = useDateMask();

  const isFormDisabled = () => {
    if (findUserByEmail) {
      return true;
    }
    return false;
  };

  const parseMaskedDate = (value: string): string | null => {
    if (!value) return null;

    if (value.length > 14) {
      value = value.slice(0, 14);
    }

    const match = value.match(/^(\d{2})\s\/\s(\d{2})\s\/\s(\d{4})$/);
    if (!match) return null;

    const [, dd, mm, yyyy] = match;
    const day = Number(dd);
    const month = Number(mm) - 1;
    const year = Number(yyyy);
    const date = new Date(Date.UTC(year, month, day));

    return date.toISOString();
  };

  const onCancel = () => {
    setIsModalOpen(false);
    form.resetFields();
    setSelectedUserType('Fisica');
    setCompanyName('');
    setSelectedGender(initialValues.gender);
  };

  const onSubmit = async () => {
    await form.validateFields();

    const values = form.getFieldsValue();

    const params: NewFarmerParams = {
      ...values,
      phone,
      birthdate: parseMaskedDate(values.birthdate),
      isCompany: selectedUserType === 'Moral',
      companyName,
      managerId: manager.id,
    };

    try {
      await dealerHttp.addFarmer(manager.dealerId, params);

      onCancel();
      notify('Farmer added successfully.');
    } catch (e) {
      console.error(e);
      notify('Farmer added failed.');
    }
  };

  const onChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    form.setFieldValue(e.currentTarget.id, e.currentTarget.value);
  };

  const onEmailChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    form.setFieldValue('email', value);
    hasLoaded.current = false;
  };

  const onPhoneChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    const value = `${e.currentTarget.value}`.slice(0, MAX_PHONE_LENGTH);
    form.setFieldValue('phone', phoneFormat(value));
    hasLoaded.current = false;
  };

  const onZipCodeChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    form.setFieldValue('zipCode', value);
    hasLoaded.current = false;
  };

  const onBirthdateChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    form.setFieldValue('birthdate', value);
    hasLoaded.current = false;
  };

  const onUserTypeChangeHandler = (value: string) => {
    setSelectedUserType(value);
    form.setFieldValue('isCompany', value === 'Moral');
  };

  const onGenderChangeHandler = (value: string) => {
    setSelectedGender(value);
    form.setFieldValue('gender', value);
  };

  const onCompanyNameChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    setCompanyName(value);
    form.setFieldValue('companyName', value);
  };

  useEffect(() => {
    if (isBirthdayValid || isPhoneValid || isZipCodeValid) {
      form.validateFields();
    }
  }, [isBirthdayValid, isPhoneValid, isZipCodeValid]);

  useEffect(() => {
    if (!hasLoaded.current) {
      if (debouncedEmail) {
        (async () => {
          const loadedUser = await userHttp.userCheckByEmail(debouncedEmail);

          setFindUserByEmail(loadedUser);
        })();
      }
    }
  }, [debouncedEmail]);

  useEffect(() => {
    if (!hasLoaded.current) {
      if (debouncedPhone) {
        (async () => {
          const loadedUser = await userHttp.userCheckByPhone(debouncedPhone);

          setFindUserByPhone(loadedUser);
        })();
      }
    }
  }, [debouncedPhone]);

  if (!manager) return null;

  return (
    <div className={clsx(cls.addFarmerWrap, className)}>
      <div className={cls.addFarmerBlock}>
        <button
          id="add-btn"
          className={cls.addFarmerBtn}
          type="button"
          onClick={() => setIsModalOpen(true)}>Add Farmer</button>
      </div>

      <Modal
        title={null}
        open={isModalOpen}
        onOk={onSubmit}
        onCancel={onCancel}
        closable={false}
        maskClosable={false}
        footer={null}
        width={1000}
      >
        <div className={cls.addFarmerFormWrap}>
          <div className={cls.formTitle}>Register a Farmer</div>
          <div className={cls.form}>
            <Form
              form={form}
              className={cls.form}
            >
              <Row gutter={48} className={cls.wrapper} align="stretch">
                <Col xs={24} lg={12} className={cls.wrapperCol}>
                  {/* --------firstName------------ */}
                  <Form.Item name="firstName" className={cls.col} help={''} rules={[{ required: true }]}>
                    <FormRow
                      title="Nombre*:"
                      width={mainColGrid}
                      rowClassName={clsx(cls.formRow, cls.mainFormRow)}
                      colTitleClassName={cls.formLabelCol}
                      colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
                    >
                      <Input
                        id="firstName"
                        className={cls.input}
                        placeholder="Ingresa en nombre*"
                        value={firstName || ''}
                        onChange={onChangeHandler}
                        maxLength={MAX_NAME_LENGTH}
                      />
                    </FormRow>
                  </Form.Item>
                  {/* ----------lastName---------- */}
                  <Form.Item name="lastName" className={cls.col} help={''} rules={[{ required: true }]}>
                    <FormRow
                      title="A. Materno*:"
                      width={mainColGrid}
                      rowClassName={clsx(cls.formRow, cls.mainFormRow)}
                      colTitleClassName={cls.formLabelCol}
                      colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
                    >
                      <Input
                        id="lastName"
                        className={cls.input}
                        placeholder="Ingresa en apellido materno*"
                        value={lastName || ''}
                        onChange={onChangeHandler}
                        maxLength={MAX_NAME_LENGTH}
                      />
                    </FormRow>
                  </Form.Item>
                  {/* ----------isCompany------------ */}
                  <Form.Item name="isCompany" className={cls.col}>
                    <FormRow
                      title="Tipo de Cliente*:"
                      width={mainColGrid}
                      rowClassName={clsx(cls.formRow, cls.mainFormRow)}
                      colTitleClassName={cls.formLabelCol}
                      colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
                    >
                      <Select
                        value={selectedUserType}
                        onChange={onUserTypeChangeHandler}
                        options={userTypeOptions}
                        className={cls.select}
                      />
                    </FormRow>
                  </Form.Item>
                  {/* ----------email------------ */}
                  <Form.Item
                    name="email"
                    help={''}
                    className={cls.col}
                    rules={[
                      () => ({
                        validator(_, value) {
                          if (!value) return Promise.reject(new Error('el campo obligatorio'));

                          const reg = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/gi;
                          if (!reg.test(value)) {
                            return Promise.reject(new Error('Por favor escribe un email correcto'));
                          }

                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <FormRow
                      title="Email*:"
                      width={mainColGrid}
                      rowClassName={clsx(cls.formRow, cls.mainFormRow)}
                      colTitleClassName={cls.formLabelCol}
                      colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
                    >
                      <Input
                        id="email"
                        className={cls.input}
                        placeholder="Ingresa el correo electronico"
                        value={email || ''}
                        onChange={onEmailChangeHandler}
                      />
                    </FormRow>
                  </Form.Item>
                  {/* ---------zipCode-------------- */}
                  <Form.Item
                    name="zipCode"
                    className={cls.col}
                    help={''}
                    rules={[
                      () => ({
                        validator(_, value) {
                          const data = value?.replace(/\D/g, '') || '';
                          if (data.length !== 5 && data.length !== 6) {
                            return Promise.reject('el campo obligatorio');
                          }

                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <FormRow
                      title="ZIP Code*:"
                      width={mainColGrid}
                      rowClassName={clsx(cls.formRow, cls.mainFormRow)}
                      colTitleClassName={cls.formLabelCol}
                      colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
                    >
                      <Input
                        id="zipCode"
                        className={cls.input}
                        placeholder="Ingresa el código postal"
                        value={numberFormat(zipCode || '' as string)}
                        onChange={onZipCodeChangeHandler}
                        maxLength={MAX_ZIP_LENGTH}
                      />
                    </FormRow>
                  </Form.Item>
                </Col>

                {/* ------------========Col========---------- */}

                <Col xs={24} lg={12} className={cls.wrapperCol}>
                  {/* ----------middleName-------------- */}
                  <Form.Item name="middleName" className={cls.col} help={''} rules={[{ required: true }]}>
                    <FormRow
                      title="A. Paterno*:"
                      width={mainColGrid}
                      rowClassName={clsx(cls.formRow, cls.mainFormRow)}
                      colTitleClassName={cls.formLabelCol}
                      colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
                    >
                      <Input
                        id="middleName"
                        className={cls.input}
                        placeholder="Ingresa en apellido paterno*"
                        value={middleName || ''}
                        onChange={onChangeHandler}
                        maxLength={MAX_NAME_LENGTH}
                      />
                    </FormRow>
                  </Form.Item>
                  {/* ----------phone-------------- */}
                  <Form.Item
                    name="phone"
                    help={''}
                    className={cls.col}
                    rules={[
                      () => ({
                        validator(_, value) {
                          const phone = value?.replace(/\D/g, '') || '';
                          if (phone.length < 10) {
                            return Promise.reject('el campo obligatorio');
                          }

                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <FormRow
                      title="Teléfono*:"
                      width={mainColGrid}
                      rowClassName={clsx(cls.formRow, cls.mainFormRow)}
                      colTitleClassName={cls.formLabelCol}
                      colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
                    >
                      <Input
                        id="phone"
                        className={cls.input}
                        placeholder="( __ ) ____ - ____"
                        value={phoneFormat(phone || '' as string)}
                        onChange={onPhoneChangeHandler}
                        maxLength={MAX_PHONE_LENGTH}
                      />
                    </FormRow>
                  </Form.Item>
                  {/* -----------companyName------------- */}
                  <Form.Item name="companyName" className={cls.col}>
                    <FormRow
                      title={'Razón Social:'}
                      width={mainColGrid}
                      rowClassName={clsx(
                        cls.formRow,
                        cls.mainFormRow,
                        selectedUserType !== 'Moral' && cls.formRowDisabled,
                      )}
                      colTitleClassName={cls.formLabelCol}
                      colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
                    >
                      <Input
                        id="companyName"
                        className={cls.input}
                        placeholder="Escribe la razón social"
                        disabled={selectedUserType !== 'Moral'}
                        value={companyName || ''}
                        onChange={onCompanyNameChangeHandler}
                      />
                    </FormRow>
                  </Form.Item>
                  {/* ----------gender-------------- */}
                  <Form.Item name="gender" className={cls.col} help={''} rules={[{ required: true }]}>
                    <FormRow
                      title="Gender*:"
                      width={mainColGrid}
                      rowClassName={clsx(cls.formRow, cls.mainFormRow)}
                      colTitleClassName={cls.formLabelCol}
                      colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
                    >
                      <Select
                        value={selectedGender}
                        onChange={onGenderChangeHandler}
                        options={genderOptions}
                        className={cls.select}
                      />
                    </FormRow>
                  </Form.Item>
                  {/* -----------birthdate------------- */}
                  <Form.Item
                    name="birthdate"
                    className={cls.col}
                    help={''}
                    rules={[
                      () => ({
                        validator(_, value) {
                          const data = value?.replace(/\D/g, '') || '';
                          if (data.length > 0 && data.length < 8) {
                            return Promise.reject('el campo obligatorio');
                          }

                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <FormRow
                      title="F. de Nacimiento:"
                      width={mainColGrid}
                      rowClassName={clsx(cls.formRow, cls.mainFormRow)}
                      colTitleClassName={cls.formLabelCol}
                      colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
                    >
                      <Input
                        id="birthdate"
                        className={cls.input}
                        placeholder="DD / MM / AAAA"
                        value={dateFormat(birthdate || '' as string)}
                        onChange={onBirthdateChangeHandler}
                      />
                    </FormRow>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </div>
          {findUserByEmail || findUserByPhone ? (
            findUserByEmail ? (
              <div className={cls.messageBlock}>
                <div className={cls.messageText}>
                  Este correo ya está en uso por otra cuenta.
                  Verifícalo de nuevo o intenta con otro correo electrónico.
                </div>
                <div className={cls.messageError}>
                  El correo electrónico es incorrecto
                </div>
              </div>
            ) : (
              <div className={cls.messageBlock}>
                <div className={cls.messageRow}>
                  <div className={cls.messageCol}>
                    <div className={cls.messageText}>
                      Este contacto ya está registrado en Verqor con el nombre
                      de “{findUserByPhone?.fullName}”, si se trata de la misma
                      persona que tratas de registrar da click en el botón para agregarlo a
                      su cartera de productores.
                    </div>
                    <div className={cls.messageWarning}>
                      De lo contrario verifica que el número de teléfono sea el correcto.
                    </div>
                  </div>
                  <div className={cls.messageCol}>
                    <Button
                      onClick={onSubmit}
                      className={clsx(cls.btn, cls.btnSubmit, isFormDisabled() && cls.btnDisable)}
                      disabled={isFormDisabled()}
                      icon={<Icon />}
                    >Agregar</Button>
                  </div>
                </div>
              </div>
            )
          ) : (
            <div className={cls.formActions}>
              <Button onClick={onCancel} className={clsx(cls.btn, cls.btnCancel)}>Cancelar</Button>
              <Button
                onClick={onSubmit}
                className={clsx(cls.btn, cls.btnSubmit, isFormDisabled() && cls.btnDisable)}
                disabled={isFormDisabled()}
              >Registrar</Button>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};
